import './buy-product-policy.scss'

import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'
import { Paths } from 'src/navigation'
import { useGetSedi } from 'src/networking/terminals'
import {
  atomDossier,
  atomFormikProductConfiguration,
  atomSedi,
  atomSelectedProduct,
} from 'src/storage'
import { InputSelectOption } from 'src/types'
import { yupStrRequired } from 'src/utils'
import * as yup from 'yup'

export const useBuyProductPolicy = () => {
  const { t } = useTranslation()

  const { crossGate: getSedi } = useGetSedi()

  const navigate = useNavigate()

  const dossier = useRecoilValue(atomDossier)
  const [selectedProduct, saveSelectedProduct] = useRecoilState(atomSelectedProduct)
  const productOptions = dossier.prodotti
  const [sedi, saveSedi] = useRecoilState(atomSedi)

  const [showWarning, setShowWarning] = useState(false)
  const [warningTitle, setWarningTitle] = useState('')
  const [warningDescription, setWarningDescription] = useState('')

  const [formikProductConfiguration, saveFormikProductConfiguration] = useRecoilState(
    atomFormikProductConfiguration
  )

  const validationSchema = yup.object({
    company: yupStrRequired,
    season: yupStrRequired,
  })

  const formikInitialValue = {
    ...formikProductConfiguration,
    season: selectedProduct?.stagioni?.[0].codProdotto.toString(),
  }

  const formik = useFormik({
    initialValues: formikInitialValue,
    validationSchema: validationSchema,
    onSubmit: () => {},
  })

  useEffect(() => {
    getSedi(
      {},
      {
        onSuccess: (response) => {
          const sedi = response?.sedi
          saveSedi(sedi)
          if (!sedi || sedi?.length === 0) {
            setWarningTitle(
              t('landing.section4.insurance_widget.warning_message.no_available_companies')
            )
            setShowWarning(true)
            return
          }

          formik.setFieldValue('company', response.sedi[0]?.code?.toString())
        },
      }
    )
  }, [])

  useEffect(() => {
    if (sedi?.length == 0 || !selectedProduct) return

    formik.setFieldValue('season', selectedProduct.prodotto.code)

    const sede = sedi.find(
      (company) => company?.code.toString() === formik?.values?.company?.toString()
    )

    const alreadyHasPolicy = (sede?.infoPolizza ?? []).some(
      (infoPolizza) => infoPolizza.code === selectedProduct.prodotto.code
    )

    const isPisWaiting = (sede?.infoPolizza ?? []).find(
      (infoPolizza) =>
        infoPolizza.code === selectedProduct.prodotto.code &&
        infoPolizza.description === 'COPERTURA IN CORSO DI PAGAMENTO PIS'
    )

    if (alreadyHasPolicy) {
      if (isPisWaiting) {
        setWarningTitle(
          t(
            'landing.section4.insurance_widget.warning_message.not_available_for_selected_company_pis'
          )
        )
        setWarningDescription(
          t(
            'landing.section4.insurance_widget.warning_message.not_available_for_selected_company_description_pis'
          )
        )
      } else {
        setWarningTitle(
          t('landing.section4.insurance_widget.warning_message.not_available_for_selected_company')
        )
        setWarningDescription(
          t(
            'landing.section4.insurance_widget.warning_message.not_available_for_selected_company_description'
          )
        )
      }
      setShowWarning(true)
    } else {
      setShowWarning(false)
    }
  }, [formik?.values?.company, sedi, selectedProduct])

  const onPressBuyInsurance = () => {
    saveFormikProductConfiguration(formik.values)
    navigate(Paths.PolicyConfiguration)
  }

  const onProductSelectedChange = (option: InputSelectOption) => {
    const productSelectedCode = option.id
    if (!productSelectedCode) return

    const newSelectedProduct = dossier.prodotti.find(
      (product) => product.prodotto.code === productSelectedCode
    )

    if (!newSelectedProduct) return

    saveSelectedProduct(newSelectedProduct)
  }

  return {
    t,
    formik,
    productOptions,
    sedi,
    showWarning,
    warningTitle,
    warningDescription,
    validationSchema,
    onPressBuyInsurance,
    onProductSelectedChange,
  }
}
